import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { PageShell, SectionHeading } from "@/components/PageShell";
import { ListingCard } from "@/components/listings/ListingCard";
import { EmptyLegCard, PartnersStrip } from "@/components/EmptyLegs";

const CATEGORY_META = {
  aircraft: { overline: "Aircraft for sale", title: "Aircraft currently represented by the Club", intro: "On and off-market mandates. Prices and full specifications are shared on application.", enquire: "/enquire/aircraft", enquireLabel: "Looking for something specific? Tell us your mission profile" },
  yacht: { overline: "Yachts", title: "Motor yachts and superyachts through our brokerage network", intro: "Charter, purchase or sale. Listings shown here are available through Aero Vista Club and our partner brokers.", enquire: "/enquire/yacht", enquireLabel: "Not seeing the right yacht? Brief us" },
};

export function ListingsPage({ category }) {
  const [items, setItems] = useState(null);
  const meta = CATEGORY_META[category];
  useEffect(() => { setItems(null); api(`/listings?category=${category}`).then(setItems).catch(() => setItems([])); }, [category]);
  return (
    <PageShell>
      <section className="pt-36 pb-24 lg:pb-32 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading overline={meta.overline} title={meta.title} testid={`listings-heading-${category}`}>{meta.intro}</SectionHeading>
          {items === null ? <p className="text-white/40 text-sm">Loading...</p> : items.length === 0 ? (
            <p className="text-white/50 text-sm" data-testid="listings-empty">No listings published right now.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`listings-grid-${category}`}>
              {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
          <div className="mt-16 p-8 lg:p-10 border border-white/10 bg-surface rounded-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
            <p className="font-serif text-2xl text-white">{meta.enquireLabel}</p>
            <Link to={meta.enquire} data-testid={`listings-enquire-cta-${category}`} className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors whitespace-nowrap">Enquire <ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function ListingDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(false);
  const [idx, setIdx] = useState(0);
  useEffect(() => { api(`/listings/${id}`).then(setListing).catch(() => setError(true)); }, [id]);

  if (error) return <PageShell><div className="pt-40 pb-32 text-center text-white/60" data-testid="listing-not-found">Listing not found. <Link to="/" className="text-gold">Back to site</Link></div></PageShell>;
  if (!listing) return <PageShell><div className="pt-40 pb-32 text-center text-white/40">Loading...</div></PageShell>;

  const photos = listing.photos || [];
  const price = "Price on application";
  const enquireUrl = `/enquire/${listing.category}?listing=${listing.id}&title=${encodeURIComponent(listing.title)}&intent=${encodeURIComponent(listing.category === "yacht" ? "Buy a yacht" : "Buy an aircraft")}`;

  return (
    <PageShell>
      <section className="pt-28 pb-24" data-testid="listing-detail-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to={listing.category === "yacht" ? "/yachts" : "/aircraft-for-sale"} className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-gold transition-colors" data-testid="listing-back-link">← All {listing.category === "yacht" ? "yachts" : "aircraft"}</Link>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="relative aspect-[16/10] bg-surface rounded-sm overflow-hidden border border-white/5">
                {photos[idx] && <img src={photoUrl(photos[idx])} alt={listing.title} className="w-full h-full object-cover" data-testid="listing-gallery-image" />}
                {photos.length > 1 && (<>
                  <button onClick={() => setIdx((idx - 1 + photos.length) % photos.length)} aria-label="Previous" data-testid="listing-gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-gold hover:text-black transition-colors"><ChevronLeft size={18} /></button>
                  <button onClick={() => setIdx((idx + 1) % photos.length)} aria-label="Next" data-testid="listing-gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-gold hover:text-black transition-colors"><ChevronRight size={18} /></button>
                </>)}
              </div>
              {photos.length > 1 && (
                <div className="mt-3 grid grid-cols-6 gap-2">
                  {photos.map((p, i) => (<button key={p.id} onClick={() => setIdx(i)} className={`aspect-[4/3] overflow-hidden rounded-sm border ${i === idx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`listing-thumb-${i}`}><img src={photoUrl(p)} alt="" className="w-full h-full object-cover" /></button>))}
                </div>
              )}
              {listing.description && <div className="mt-10 max-w-3xl"><p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Overview</p><p className="text-white/70 font-light leading-relaxed whitespace-pre-line">{listing.description}</p></div>}
            </div>
            <aside className="lg:col-span-4">
              <div className="sticky top-28 bg-surface border border-white/10 rounded-sm p-8">
                <div className="flex gap-2 mb-4">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">{listing.category}</span>
                  {listing.discreet && <span className="text-[10px] uppercase tracking-[0.2em] text-gold">· Discreet / off-market</span>}
                  {listing.status !== "live" && <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">· {listing.status.replace("_", " ")}</span>}
                </div>
                <h1 className="font-serif text-3xl lg:text-4xl text-white leading-tight" data-testid="listing-title">{listing.title}</h1>
                <p className="text-sm text-white/50 mt-2">{[listing.year, listing.location].filter(Boolean).join(" · ")}</p>
                <p className="font-serif text-3xl text-gold mt-6" data-testid="listing-detail-price">{price}</p>
                {listing.summary && <p className="mt-4 text-sm text-white/70 font-light leading-relaxed">{listing.summary}</p>}
                {Object.keys(listing.specs || {}).length > 0 && (
                  <dl className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-xs">
                    {Object.entries(listing.specs).map(([k, v]) => (<div key={k}><dt className="text-white/40 uppercase tracking-wider text-[9px]">{k}</dt><dd className="text-white/85 mt-0.5">{v}</dd></div>))}
                  </dl>
                )}
                <Link to={enquireUrl} data-testid="listing-detail-enquire-button" className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold text-black text-xs uppercase tracking-[0.2em] py-4 rounded-sm hover:bg-gold-light transition-colors">
                  Enquire for price & details <ArrowUpRight size={14} />
                </Link>
                {listing.is_example && <p className="mt-4 text-[10px] text-white/30">Example listing shown for illustration until the Club replaces it.</p>}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function EmptyLegsPage() {
  const [legs, setLegs] = useState(null);
  useEffect(() => { api("/empty-legs").then(setLegs).catch(() => setLegs([])); }, []);
  return (
    <PageShell>
      <section className="pt-36 pb-24 lg:pb-32 min-h-screen bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading overline="Empty legs & offers" title="Every published positioning flight and charter offer" testid="empty-legs-page-heading">
            Pricing on enquiry for the whole aircraft; the team confirms availability before booking. Flights disappear automatically after departure.
          </SectionHeading>
          {legs === null ? <p className="text-white/40 text-sm">Loading...</p> : legs.length === 0 ? (
            <p className="text-white/50 text-sm" data-testid="empty-legs-page-empty">No empty legs right now. <Link to="/enquire/charter" className="text-gold">Request a flight</Link> and we will source options.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="empty-legs-page-grid">{legs.map((l, i) => <EmptyLegCard key={l.id} leg={l} index={i} />)}</div>
          )}
          <PartnersStrip />
        </div>
      </section>
    </PageShell>
  );
}
